import React, { useLayoutEffect, useRef, useState } from 'react';

const GAP = 14;
const VIEWPORT_PAD = 8;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function placeCard(targetRect, placement, width, height) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top;
  let left;
  let used = placement;

  switch (placement) {
    case 'bottom':
      top = targetRect.bottom + GAP;
      left = targetRect.left + targetRect.width / 2 - width / 2;
      if (top + height > vh - VIEWPORT_PAD) {
        used = 'top';
        top = targetRect.top - GAP - height;
      }
      break;
    case 'left':
      top = targetRect.top + targetRect.height / 2 - height / 2;
      left = targetRect.left - GAP - width;
      if (left < VIEWPORT_PAD) {
        used = 'right';
        left = targetRect.right + GAP;
      }
      break;
    case 'right':
      top = targetRect.top + targetRect.height / 2 - height / 2;
      left = targetRect.right + GAP;
      if (left + width > vw - VIEWPORT_PAD) {
        used = 'left';
        left = targetRect.left - GAP - width;
      }
      break;
    case 'top':
    default:
      used = 'top';
      top = targetRect.top - GAP - height;
      left = targetRect.left + targetRect.width / 2 - width / 2;
      if (top < VIEWPORT_PAD) {
        used = 'bottom';
        top = targetRect.bottom + GAP;
      }
      break;
  }

  left = clamp(left, VIEWPORT_PAD, Math.max(VIEWPORT_PAD, vw - width - VIEWPORT_PAD));
  top = clamp(top, VIEWPORT_PAD, Math.max(VIEWPORT_PAD, vh - height - VIEWPORT_PAD));
  return { top, left, placement: used };
}

function arrowOffset(targetRect, card, placement) {
  if (placement === 'left' || placement === 'right') {
    const y = targetRect.top + targetRect.height / 2 - card.top;
    return { '--sg-coach-arrow-y': `${clamp(y, 16, card.height - 16)}px` };
  }
  const x = targetRect.left + targetRect.width / 2 - card.left;
  return { '--sg-coach-arrow-x': `${clamp(x, 16, card.width - 16)}px` };
}

/**
 * Presentational coachmark card + target highlight.
 * Overlay click / Escape (handled by provider) dismisses — does not focus-trap.
 */
export default function Coachmark({
  step,
  targetRect,
  stepIndex,
  stepCount,
  onNext,
  onSkip,
}) {
  const cardRef = useRef(null);
  const [box, setBox] = useState(null);
  const placement = step?.placement || 'bottom';
  const isLast = stepIndex >= stepCount - 1;

  useLayoutEffect(() => {
    if (!targetRect || !cardRef.current) return undefined;
    const measure = () => {
      const { offsetWidth, offsetHeight } = cardRef.current;
      setBox(placeCard(targetRect, placement, offsetWidth, offsetHeight));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [targetRect, placement, step?.id]);

  if (!step || !targetRect) return null;

  const spotPad = 6;
  const cardStyle = box
    ? {
        top: box.top,
        left: box.left,
        ...arrowOffset(targetRect, { ...box, width: cardRef.current?.offsetWidth || 0, height: cardRef.current?.offsetHeight || 0 }, box.placement),
      }
    : { visibility: 'hidden', top: 0, left: 0 };

  return (
    <>
      <div
        className="sg-coach-overlay"
        aria-hidden="true"
        onClick={onSkip}
      />
      <div
        className="sg-coach-spot"
        style={{
          top: targetRect.top - spotPad,
          left: targetRect.left - spotPad,
          width: targetRect.width + spotPad * 2,
          height: targetRect.height + spotPad * 2,
        }}
      />
      <div
        ref={cardRef}
        className="sg-coach"
        role="dialog"
        aria-labelledby="sg-coach-title"
        aria-describedby="sg-coach-body"
        data-placement={box?.placement || placement}
        data-sg-coach-step={step.id}
        style={cardStyle}
      >
        <span className="sg-coach-arrow" aria-hidden />
        <p className="sg-coach-meta">
          {stepIndex + 1} of {stepCount}
        </p>
        <h3 id="sg-coach-title" className="sg-coach-title">
          {step.title}
        </h3>
        {step.body ? (
          <p id="sg-coach-body" className="sg-coach-body">
            {step.body}
          </p>
        ) : null}
        <div className="sg-coach-actions">
          <button type="button" className="btn btn-secondary" onClick={onSkip}>
            Skip
          </button>
          <button type="button" className="btn btn-primary" onClick={onNext}>
            {isLast ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
}
